import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent 
} from "../ui/dialog";
import { X, Loader2 } from 'lucide-react';
import { Button } from "../ui/button";

interface ResolutionDetails {
  issue: string;
  rootCause: string;
  solution: string;
  nextSteps: string[];
}

interface ResolutionDetailsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (details: ResolutionDetails) => Promise<void>;
  requesterName?: string;
  isSubmitting?: boolean;
}

export const ResolutionDetailsModal: React.FC<ResolutionDetailsModalProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  requesterName = 'The requester',
  isSubmitting = false
}) => {
  const [details, setDetails] = useState<ResolutionDetails>({
    issue: "",
    rootCause: "",
    solution: "",
    nextSteps: [""]
  });

  const handleAddStep = () => {
    setDetails(prev => ({ ...prev, nextSteps: [...prev.nextSteps, ""] }));
  };

  const handleRemoveStep = (index: number) => {
    setDetails(prev => ({ 
      ...prev, 
      nextSteps: prev.nextSteps.filter((_, i) => i !== index) 
    }));
  };

  const handleStepChange = (index: number, value: string) => {
    const newSteps = [...details.nextSteps];
    newSteps[index] = value;
    setDetails(prev => ({ ...prev, nextSteps: newSteps }));
  };

  const handleSubmit = () => {
    const cleanedDetails = {
      ...details,
      nextSteps: details.nextSteps.filter(step => step.trim() !== "")
    };
    onSubmit(cleanedDetails);
  };

  const isInvalid = !details.issue.trim() || 
                    !details.rootCause.trim() || 
                    !details.solution.trim() || 
                    details.nextSteps.every(step => step.trim() === "");

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent hideClose className="max-w-3xl bg-card border border-border rounded-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Resolution Details</h3>
          <button 
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-accent rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">Please provide resolution details before marking this request as resolved.</p>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-blue-800">
            <strong>{requesterName}</strong> will receive an email notification with these resolution details.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Issue <span className="text-destructive">*</span>
            </label>
            <textarea 
              placeholder="What was the problem?" 
              rows={2} 
              className="w-full px-4 py-2 bg-slate-50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              value={details.issue}
              onChange={(e) => setDetails(prev => ({ ...prev, issue: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Root Cause <span className="text-destructive">*</span>
            </label>
            <textarea 
              placeholder="Why did it happen?" 
              rows={2} 
              className="w-full px-4 py-2 bg-slate-50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              value={details.rootCause}
              onChange={(e) => setDetails(prev => ({ ...prev, rootCause: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Solution <span className="text-destructive">*</span>
            </label>
            <textarea 
              placeholder="How was it fixed?" 
              rows={3} 
              className="w-full px-4 py-2 bg-slate-50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              value={details.solution}
              onChange={(e) => setDetails(prev => ({ ...prev, solution: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Next Steps <span className="text-destructive">*</span>
            </label>
            <div className="space-y-2">
              {details.nextSteps.map((step, index) => (
                <div key={index} className="flex gap-2">
                  <input 
                    type="text" 
                    placeholder={`Step ${index + 1}`} 
                    className="flex-1 px-4 py-2 bg-slate-50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring" 
                    value={step}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                  />
                  {details.nextSteps.length > 1 && (
                    <button 
                      onClick={() => handleRemoveStep(index)}
                      className="p-2 hover:bg-accent rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button 
                onClick={handleAddStep}
                className="text-sm text-primary hover:underline"
              >
                + Add another step
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6 p-6 -mx-6 -mb-6 border-t border-border bg-gray-50/50">
          <Button 
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-11 text-sm font-bold border-slate-200 text-slate-700 hover:bg-white transition-all bg-white"
          >
            Cancel
          </Button>
          <Button 
            disabled={isSubmitting || isInvalid}
            onClick={handleSubmit}
            className="flex-1 h-11 bg-primary text-primary-foreground text-sm font-bold shadow-md hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Mark as Resolved
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
